import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core';

import { ButtonSquigglies } from './ButtonSquigglies';
import { Card, CardProps } from './Card';
import { TextEditor, TextEditorProps } from './TextEditor';
import { Toolbar } from './Toolbar';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    textEditor: {
      border: 'none',
    },
    toolbar: {
      padding: `0 ${unit * 2}px  ${unit * 2}px`,
    },
  });

const messages = defineMessages({
  placeholder: {
    id: 'components.v2.CardFollowupTemplate.placeholder',
    defaultMessage: 'Type your message',
  },
  tooltipSquigglies: {
    id: 'components.v2.CardFollowupTemplate.tooltipSquigglies',
    defaultMessage: 'Squigglies',
  },
});

export interface CardFollowupTemplateProps extends Omit<CardProps, 'classes' | 'onChange'>, WithStyles<typeof styles> {
  value: string;
  squigglies?: TextEditorProps['squigglies'];
  helperText?: TextEditorProps['helperText'];
  onChange?: (value: string) => void;
  textEditorVariant?: TextEditorProps['variant'];
  error?: string;
  invalid?: boolean;
  touched?: boolean;
}

const CardFollowupTemplate: React.FC<CardFollowupTemplateProps> = ({
  classes,
  value,
  onChange,
  onBlur,
  error,
  invalid,
  squigglies,
  helperText,
  textEditorVariant = 'default',
  touched,
  ...rest
}) => {
  const { formatMessage } = useIntl();

  return (
    <Card elevation={0} square {...rest}>
      <TextEditor
        className={classes.textEditor}
        initialText={value}
        placeholder={formatMessage(messages.placeholder)}
        squigglies={squigglies}
        variant={textEditorVariant}
        error={invalid && touched && Boolean(error)}
        helperText={invalid && touched && Boolean(error) ? error : helperText}
        autoFocus
        onBlur={onBlur}
        onContentChange={(newContentState) => onChange && onChange(newContentState.getPlainText())}
        bottomToolbar={({ handleSquigglySelected }) => (
          <Toolbar variant="dense" className={classes.toolbar}>
            {squigglies && (
              <ButtonSquigglies
                color="primary"
                iconProps={{ fontSize: 'small' }}
                squigglies={squigglies}
                includePatientFormTemplates
                includeLumabotFlowTemplates
                onSquigglySelected={handleSquigglySelected}
                tooltipTitle={formatMessage(messages.tooltipSquigglies)}
              />
            )}
          </Toolbar>
        )}
      />
    </Card>
  );
};

export default withStyles(styles)(CardFollowupTemplate);
